/**
 * Mobile Apps (HR + admin, 2026-10-07 Manak) — the latest APK of every AURA app,
 * read from the same release list the phones update from. Download, copy the
 * link to send to an employee, or show a QR to scan from their phone.
 */
import { useEffect, useState } from 'react';
import { Copy, Download, QrCode, X } from 'lucide-react';
import { DeskState } from '@/pages/dashboard/roles/ProfileDeskParts';
import { shortDate, useMobileApps, type MobileApp } from './appOverviewData';

export function ForcedTag() {
  return <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700" title="Phones on an older version must update before they can carry on">FORCED</span>;
}

function QrDialog({ app, onClose }: { app: MobileApp; onClose: () => void }) {
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    import('qrcode')
      .then((QRCode) => (QRCode.default ?? QRCode).toString(app.apkUrl, { type: 'svg', margin: 1, width: 240, errorCorrectionLevel: 'M' }))
      .then((s) => { if (alive) setSvg(s); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [app.apkUrl]);
  return <div role="dialog" aria-modal="true" aria-label={`QR code for ${app.name}`} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 max-sm:p-0" onClick={onClose}>
    <div className="w-full max-w-xs rounded-xl border border-gray-200 bg-white p-5 text-center shadow-lg max-sm:h-full max-sm:max-w-none max-sm:rounded-none max-sm:border-0" onClick={(e) => e.stopPropagation()}>
      <div className="mb-3 flex items-center justify-between"><strong>{app.name} v{app.versionName}</strong><button aria-label="Close" onClick={onClose} className="rounded p-1 hover:bg-gray-100 max-sm:-mr-2 max-sm:flex max-sm:min-h-[40px] max-sm:min-w-[40px] max-sm:items-center max-sm:justify-center"><X size={16} /></button></div>
      {failed ? <p role="alert">Could not draw the QR code. Use Copy link instead.</p>
        : svg ? <div data-testid="qr-svg" className="mx-auto w-60" dangerouslySetInnerHTML={{ __html: svg }} />
        : <p role="status">Drawing QR…</p>}
      <p className="mt-3 text-sm text-gray-600">Scan with the employee&apos;s phone camera to download.</p>
    </div>
  </div>;
}

export function MobileAppsPage() {
  const apps = useMobileApps();
  const [qr, setQr] = useState<MobileApp | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (app: MobileApp) => {
    try {
      await navigator.clipboard.writeText(app.apkUrl);
      setCopied(app.key);
    } catch {
      window.prompt('Copy this link', app.apkUrl);
    }
  };
  return <div className="mx-auto max-w-screen-xl space-y-6 p-4 max-sm:p-0 md:p-6">
    <header><h1 className="text-2xl font-semibold">Mobile Apps</h1><p className="text-gray-600">Download the latest app, or share the link / QR with an employee.</p></header>
    <DeskState resource={apps} />
    {apps.data && (apps.data.length === 0
      ? <p className="rounded-xl border border-gray-200 bg-white p-5">No app releases are published yet.</p>
      : <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,260px),1fr))] gap-4">
        {apps.data.map((app) => <section key={app.key} aria-label={app.name} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-2"><h2 className="text-lg font-semibold">{app.name}</h2>{app.forced && <ForcedTag />}</div>
          <p className="mt-1 text-sm text-gray-600">v{app.versionName}{app.releasedAt ? ` · ${shortDate(app.releasedAt)}` : ''}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a href={app.apkUrl} download className="inline-flex items-center gap-1 rounded bg-green-800 px-3 py-1.5 text-sm text-white max-sm:min-h-[40px]"><Download size={14} /> Download</a>
            <button onClick={() => copy(app)} className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm max-sm:min-h-[40px]"><Copy size={14} /> {copied === app.key ? 'Copied' : 'Copy link'}</button>
            <button onClick={() => setQr(app)} className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm max-sm:min-h-[40px]"><QrCode size={14} /> QR</button>
          </div>
        </section>)}
      </div>)}
    {qr && <QrDialog app={qr} onClose={() => setQr(null)} />}
  </div>;
}
